import type { CSSProperties, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SERVICES_COMPTABLES } from '@artosera/shared';
import { Button, Select } from '@artosera/ui';
import { analyserPourcentage, pourcentageEnSaisie } from '../../lib/montant.js';
import type {
  BeneficiaireSaisie,
  GalerieListe,
  TypeRecetteId,
  TypeRecetteVue,
  VersionGrille,
} from '../../types.js';
import commun from '../VueDAigle/commun.module.css';
import { LienConsole, Section } from '../VueDAigle/composants.js';
import { useFormats } from '../VueDAigle/formats.js';
import { styleRang } from './donnees.js';
import styles from './Comptabilite.module.css';
import { Exclusions } from './Exclusions.js';
import { TypesRecette } from './TypesRecette.js';
import { useNomType } from './nomsTypes.js';
import type { Brouillon } from './useBrouillon.js';

/** Un pourcentage qui se règle au curseur et au clavier : la saisie reste libre pendant la frappe. */
function ChampPourcent({
  valeur,
  definie,
  onChange,
  etiquette,
}: {
  valeur: number;
  definie: boolean;
  /** Rend vrai quand la valeur a été ramenée sous le plafond. */
  onChange: (points: number) => boolean;
  etiquette: string;
}): JSX.Element {
  // Le texte brut n'existe que pendant la frappe ; sinon le champ montre la valeur.
  const [brut, setBrut] = useState<string | null>(null);
  const affiche = brut ?? (definie ? pourcentageEnSaisie(valeur) : '');
  return (
    <span className={styles.avecSuffixe}>
      <input
        className={styles.champPourcent}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        aria-label={etiquette}
        placeholder="—"
        value={affiche}
        onChange={(evenement) => {
          const saisie = evenement.target.value;
          setBrut(saisie);
          const lecture = analyserPourcentage(saisie);
          if (lecture.ok && lecture.valeur !== null) {
            if (onChange(lecture.valeur)) setBrut(null);
          }
        }}
        onBlur={() => setBrut(null)}
      />
      <span className={styles.suffixe} aria-hidden="true">
        %
      </span>
    </span>
  );
}

function Historique({
  versions,
  service,
  moisCourant,
  libelles,
}: {
  versions: readonly VersionGrille[];
  service: TypeRecetteId;
  moisCourant: string;
  libelles: ReadonlyMap<string, string>;
}): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const liste = versions
    .filter((v) => v.service === service)
    .sort((a, b) => b.depuisMois.localeCompare(a.depuisMois));
  if (liste.length === 0) {
    return <span className={styles.histoVide}>{t('comptabilite.grille.aucuneVersion')}</span>;
  }
  const enVigueur = liste.find((v) => v.depuisMois <= moisCourant)?.id;
  return (
    <details className={styles.histo}>
      <summary>{t('comptabilite.grille.versions', { count: liste.length })}</summary>
      <ul>
        {liste.map((version) => {
          const etat =
            version.id === enVigueur
              ? 'enVigueur'
              : version.depuisMois > moisCourant
                ? 'aVenir'
                : 'remplacee';
          return (
            <li key={version.id}>
              <span className={styles.histoMois}>
                {t('comptabilite.grille.depuis', { mois: f.moisAnnee(version.depuisMois) })}
              </span>
              <span className={commun.celluleMuette}>{t(`comptabilite.grille.etat.${etat}`)}</span>
              <span className={styles.histoParts}>
                {version.parts
                  .map(
                    (part) =>
                      `${libelles.get(part.beneficiaireId) ?? part.libelle} ${f.pourcentDePoints(part.partPointsDeBase)}`,
                  )
                  .join(' · ')}
              </span>
              {version.moisClotures.length > 0 ? (
                <span className={commun.celluleMuette}>
                  {t('comptabilite.grille.figee', { count: version.moisClotures.length })}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

/**
 * Les pourcentages par type de recette (direction, 2FA hors sujet : c'est de
 * la saisie). Une ligne par type — les cinq services, puis les types créés —,
 * un curseur et un champ par bénéficiaire sauf
 * celui qui prend le reste : sa part se recalcule seule et le total ne dépasse
 * jamais 100 %. « Enregistrer » crée une version applicable à partir d'un mois
 * (ou corrige celle qui commence ce mois-là) ; les mois clôturés ne bougent pas.
 */
export function Grille({
  brouillon,
  versions,
  moisCourant,
  numero,
  beneficiaires = [],
  galeries = [],
  lignes = SERVICES_COMPTABLES,
  types = [],
}: {
  brouillon: Brouillon;
  versions: readonly VersionGrille[];
  moisCourant: string;
  numero: string;
  /** Les types que la grille règle, dans l'ordre de ses lignes. */
  lignes?: readonly TypeRecetteId[];
  /** Tous les types de recette, pour leur gestion (créer, renommer, archiver). */
  types?: readonly TypeRecetteVue[];
  /** Les bénéficiaires du partage, avec leurs galeries exclues. */
  beneficiaires?: readonly BeneficiaireSaisie[];
  /** Le parc, pour nommer et choisir les galeries exclues. */
  galeries?: readonly GalerieListe[];
}): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const nomType = useNomType();
  const [plafond, setPlafond] = useState<TypeRecetteId | null>(null);
  const { resteId, reglables, libelles } = brouillon;
  const nom = (id: string): string => libelles.get(id) ?? id;
  const modifies = brouillon.servicesModifies.length;

  const changer = (service: TypeRecetteId, id: string, points: number): boolean => {
    const ramene = brouillon.changer(service, id, points);
    setPlafond(ramene ? service : null);
    return ramene;
  };

  return (
    <Section
      id="pourcentages"
      numero={numero}
      titre={t('comptabilite.grille.titre')}
      note={
        resteId !== null
          ? t('comptabilite.grille.note', { reste: nom(resteId) })
          : t('comptabilite.grille.noteVide')
      }
    >
      {resteId === null ? (
        <p className={commun.note}>
          {t('comptabilite.grille.sansBeneficiaire')}{' '}
          <LienConsole vers="/associes">{t('comptabilite.grille.ouvrirPartage')}</LienConsole>
        </p>
      ) : (
        <>
          <div className={styles.resteChoix}>
            <label className={styles.etiquetteChamp} htmlFor="comptabilite-reste">
              {t('comptabilite.grille.resteChoix')}
            </label>
            <Select
              id="comptabilite-reste"
              options={brouillon.ordre.map((id) => ({ value: id, label: nom(id) }))}
              value={resteId}
              onValueChange={brouillon.choisirReste}
            />
          </div>

          <div
            className={styles.grille}
            style={{ '--n': reglables.length } as CSSProperties}
            role="group"
            aria-label={t('comptabilite.grille.aria')}
          >
            <div className={`${styles.grilleLigne} ${styles.grilleTete}`} aria-hidden="true">
              <span>{t('comptabilite.grille.colService')}</span>
              {reglables.map((id) => (
                <span
                  key={id}
                  className={styles.teteRang}
                  style={styleRang(brouillon.ordre.indexOf(id))}
                >
                  {nom(id)}
                </span>
              ))}
              <span className={styles.teteRang} style={styleRang(brouillon.ordre.indexOf(resteId))}>
                {t('comptabilite.grille.colReste', { nom: nom(resteId) })}
              </span>
            </div>

            {lignes.map((service) => {
              const { definies, valeurs } = brouillon.parts(service);
              const nomService = nomType(service);
              return (
                <div
                  key={service}
                  className={styles.grilleLigne}
                  role="group"
                  aria-label={nomService}
                  data-modifie={brouillon.modifie(service) || undefined}
                >
                  <div className={styles.serviceCellule}>
                    <span className={styles.serviceNom}>{nomService}</span>
                    {!definies ? (
                      <span className={`${commun.pastille} ${commun.pastilleAmbre}`}>
                        {t('comptabilite.grille.sansGrille')}
                      </span>
                    ) : null}
                    <Historique
                      versions={versions}
                      service={service}
                      moisCourant={moisCourant}
                      libelles={libelles}
                    />
                  </div>
                  {reglables.map((id) => {
                    const points = valeurs[id] ?? 0;
                    const plafondPoints =
                      10_000 -
                      reglables.reduce(
                        (total, autre) => total + (autre === id ? 0 : (valeurs[autre] ?? 0)),
                        0,
                      );
                    return (
                      <div
                        key={id}
                        className={styles.curseur}
                        style={styleRang(brouillon.ordre.indexOf(id))}
                      >
                        <span className={styles.curseurEtiquette}>{nom(id)}</span>
                        <input
                          className={styles.glissiere}
                          type="range"
                          min={0}
                          max={plafondPoints / 100}
                          step={0.5}
                          value={points / 100}
                          aria-label={t('comptabilite.grille.curseur', {
                            partie: nom(id),
                            service: nomService,
                          })}
                          onChange={(evenement) =>
                            changer(service, id, Math.round(Number(evenement.target.value) * 100))
                          }
                        />
                        <ChampPourcent
                          valeur={points}
                          definie={definies}
                          etiquette={t('comptabilite.grille.champ', {
                            partie: nom(id),
                            service: nomService,
                          })}
                          onChange={(valeur) => changer(service, id, valeur)}
                        />
                      </div>
                    );
                  })}
                  <div className={styles.reste}>
                    <span className={styles.curseurEtiquette}>
                      {t('comptabilite.grille.colReste', { nom: nom(resteId) })}
                    </span>
                    <span
                      className={styles.resteValeur}
                      style={styleRang(brouillon.ordre.indexOf(resteId))}
                    >
                      {definies ? f.pourcentDePoints(valeurs[resteId] ?? 0) : '—'}
                    </span>
                    <div className={styles.resteBarre} aria-hidden="true">
                      {brouillon.ordre.map((id) => (
                        <span
                          key={id}
                          style={{
                            ...styleRang(brouillon.ordre.indexOf(id)),
                            width: `${String((definies ? (valeurs[id] ?? 0) : 0) / 100)}%`,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <p className={styles.plafond} role="status">
            {plafond !== null
              ? t('comptabilite.grille.plafond', { service: nomType(plafond) })
              : ''}
          </p>

          <div className={styles.actions}>
            <div>
              <label className={styles.etiquetteChamp} htmlFor="comptabilite-depuis">
                {t('comptabilite.grille.depuisChamp')}
              </label>
              <Select
                id="comptabilite-depuis"
                options={brouillon.departs.map((mois) => ({
                  value: mois,
                  label: t(
                    mois === moisCourant
                      ? 'comptabilite.grille.departCourant'
                      : mois === brouillon.departs[0]
                        ? 'comptabilite.grille.departACloturer'
                        : 'comptabilite.grille.departSimple',
                    { mois: f.moisLong(mois) },
                  ),
                }))}
                value={brouillon.depuis}
                onValueChange={brouillon.choisirDepuis}
              />
            </div>
            <Button
              loading={brouillon.enCours}
              disabled={modifies === 0}
              onClick={() => void brouillon.enregistrer()}
            >
              {t('comptabilite.grille.enregistrer')}
            </Button>
            <Button variant="secondary" disabled={modifies === 0} onClick={brouillon.annuler}>
              {t('comptabilite.grille.annuler')}
            </Button>
          </div>
          {brouillon.erreur ? (
            <p className={styles.erreur} role="alert">
              {brouillon.erreur}
            </p>
          ) : null}
          {brouillon.enregistree ? (
            <p className={styles.succes} role="status">
              {t('comptabilite.grille.enregistree', {
                count: brouillon.enregistree.services,
                mois: f.moisLong(brouillon.enregistree.depuis),
              })}
            </p>
          ) : null}
          <TypesRecette types={types} />
          <Exclusions
            beneficiaires={beneficiaires}
            galeries={galeries}
            resteId={resteId}
            resteDesigne={brouillon.resteDesigne}
            libelles={libelles}
            departs={brouillon.departs}
          />
        </>
      )}
    </Section>
  );
}
